import React from "react";
import "./SoilTriangle.scss";
import { gettext } from "../../shared/utils/legacy";
import { Vector3D, designs, DesignType, Category } from "./SoilTriangleDesigns";

const Arrow = ({ e, factor }: { e: Array<number>; factor: number }) => (
  <line
    x1={e[0] * factor}
    y1={e[1] * factor}
    x2={e[2] * factor}
    y2={e[3] * factor}
    markerEnd="url(#arrow_head)"
    style={{ stroke: "#696969" }}
  />
);
type ALabelProps = {
  e: Array<number>;
  factor: number;
  label: string;
};
const ALabel = ({ e, factor, label }: ALabelProps) => (
  <text
    transform={`translate(${e[0] * factor},${e[1] * factor}) rotate(${e[2]})`}
    dy="8"
    textAnchor="middle"
    style={{ fill: "#696969" }}
  >
    {label}
  </text>
);
const factorH = Math.sqrt(0.75);
const Mosaic = ({
  points,
  color,
  factor,
  stroke = "#fff",
}: {
  points: Array<Vector3D>;
  color: string;
  factor: number;
  stroke: string;
}) => (
  <path
    d={`M${points
      .map(
        (p: Array<number>) => `${factor * (p[1] + p[2] / 2)},${(p[0] + p[1]) * factor * factorH}`
      )
      .join("L")}Z`}
    fill={color}
    stroke={stroke}
  />
);
const MLabel = ({ points, text, factor, textBreak }: any) => {
  const c: Vector3D = [0, 0, 0];
  for (let p of points) {
    p.forEach((val: number, i: number) => {
      c[i] += val / points.length;
    });
  }
  const centroid = [factor * (c[1] + c[2] / 2), (c[0] + c[1]) * factor * factorH],
    words = textBreak ? text.split(" ") : [text];

  return (
    <g transform={`translate(${centroid[0]},${centroid[1] - (6 + 6 * words.length)})`}>
      <text>
        {words.map((text: string, i: number) => (
          <tspan key={i} x="0" dy="14">
            {text}
          </tspan>
        ))}
      </text>
    </g>
  );
};

type SoilCode = {
  name: string;
  ediCode?: string;
};

export interface SoilTriangleProps {
  triangle: DesignType;
  selected?: string;
  size?: number;
  textBreak?: boolean;
  onChange?: (soil: SoilCode | null) => void;
  className?: string;
}

function SoilTriangle({
  triangle,
  selected = "",
  size = 300,
  textBreak = false,
  onChange,
  className,
}: SoilTriangleProps) {
  const design = designs[triangle];
  const factor = size / 100;
  return (
    <div className={`st-triangle ${className || ""}`}>
      <svg width={size} height={size}>
        <defs>
          <marker
            id="arrow_head"
            markerHeight="8"
            markerWidth="8"
            orient="auto"
            refX="5"
            refY="0"
            viewBox="-5 -5 10 10"
          >
            <path d="M 0,0 m -5,-5 L 5,0 L -5,5 Z" style={{ fill: "#696969" }} />
          </marker>
        </defs>
        <g className="soiltriangle" style={onChange ? { cursor: "pointer" } : {}}>
          {design.categories.map(({ name, color, points, ediCode }: Category, index: number) => {
            const label = gettext(name),
              sel = selected === ediCode;
            return (
              <g
                key={index}
                className={`soiltype ${name.replace(/ /g, "_")}${sel ? " selected" : ""}`}
                style={{ opacity: sel ? 1 : 0.3 }}
                onClick={onChange && (() => onChange(sel ? null : { name, ediCode }))}
              >
                <Mosaic color={color} stroke="#fff" points={points} factor={factor} />
                <MLabel text={label} points={points} factor={factor} textBreak={textBreak} />
              </g>
            );
          })}
        </g>
        {[
          [57, 0, 100, 77],
          [0, 77, 43, 0],
          [100, 93, 0, 93],
        ].map((e, index) => (
          <Arrow key={index} e={e} factor={factor} />
        ))}
        {[
          [14, 43, -60],
          [84, 40, 60],
          [50, 95, 0],
        ].map((e, index) => (
          <ALabel key={index} e={e} factor={factor} label={gettext(design.labels[index])} />
        ))}
      </svg>
    </div>
  );
}
export default SoilTriangle;
